import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { MultiLineChart, RangeSelector, Sparkline } from "@/components/Charts";
import { NotesCard, AlertsCard } from "@/components/SymbolMetaCards";
import { usePrivacy } from "@/context/PrivacyContext";
import { fmtCurrency, fmtPct, fmtNumber, fmtCompact, fmtDate, gainClass } from "@/lib/format";
import { TXN_BADGE, COMPARE_COLORS } from "@/lib/constants";
import { ArrowLeft, X, Plus, ChevronDown } from "lucide-react";

function Stat({ label, value, cls }) {
    return <div><div className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</div><div className={`num font-semibold mt-0.5 ${cls || ""}`}>{value}</div></div>;
}

export default function SecurityDetail({ watchlist = false }) {
    const { ticker } = useParams();
    const navigate = useNavigate();
    const { mask } = usePrivacy();
    const [range, setRange] = useState("1D");
    const [compare, setCompare] = useState(["SP500", "NASDAQ"]);
    const [addInput, setAddInput] = useState("");
    const [showTrades, setShowTrades] = useState(false);

    const detail = useQuery({ queryKey: ["sec", ticker], queryFn: async () => (await api.get(`/securities/${ticker}`)).data });
    const hist = useQuery({ queryKey: ["sec-hist", ticker, range, compare], queryFn: async () => (await api.get(`/securities/${ticker}/history?range=${range}&compare=${compare.join(",")}`)).data });
    const vol = useQuery({ queryKey: ["sec-vol", ticker], queryFn: async () => (await api.get(`/securities/${ticker}/volatility?range=1Y`)).data });

    const info = detail.data?.info;
    const quote = detail.data?.quote;
    const pos = detail.data?.position;

    // build normalized % change series
    const intraday = range === "1D";
    const seriesKeys = [ticker, ...compare];
    const merged = {};
    const addSeries = (name, arr) => {
        if (!arr?.length) return;
        const base = arr[0].close;
        arr.forEach((pt) => {
            const d = intraday ? pt.date : pt.date.slice(0, 10);
            merged[d] ||= { date: d };
            merged[d][name] = ((pt.close / base) - 1) * 100;
        });
    };
    if (hist.data) {
        addSeries(ticker, hist.data.main);
        Object.entries(hist.data.compare || {}).forEach(([k, v]) => addSeries(k, v));
    }
    const chartData = Object.values(merged).sort((a, b) => a.date.localeCompare(b.date));

    // Buy (green) / Sell (red) trade markers, placed on the ticker's line within the visible range
    const tradeTxns = (detail.data?.transactions || []).filter(
        (t) => ["BUY", "SELL"].includes((t.transaction_type || "").toUpperCase()));
    const realizedSales = detail.data?.realized || [];
    const realizedPl = detail.data?.realized_pl;
    const splits = detail.data?.splits || [];
    // cumulative split factor for trades dated before later splits
    const splitFactor = (dateStr) => splits.reduce((f, s) => (s.date > (dateStr || "").slice(0, 10) ? f * s.ratio : f), 1);
    const ratioText = (r) => (r >= 1 ? `${+r.toFixed(r % 1 ? 2 : 0)}:1` : `1:${+(1 / r).toFixed((1 / r) % 1 ? 2 : 0)}`);
    const anySplitAdj = tradeTxns.some((t) => splitFactor(t.transaction_date) !== 1);
    const markers = [];
    if (!intraday && chartData.length) {
        const minK = chartData[0].date, maxK = chartData[chartData.length - 1].date;
        for (const t of tradeTxns) {
            const dk = (t.transaction_date || "").slice(0, 10);
            if (!dk || dk < minK || dk > maxK) continue;
            const pt = chartData.find((d) => d.date >= dk) || chartData[chartData.length - 1];
            const y = pt[ticker];
            if (y == null) continue;
            const isBuy = (t.transaction_type || "").toUpperCase() === "BUY";
            const f = splitFactor(t.transaction_date);
            let tip = `${isBuy ? "BUY" : "SELL"} · ${fmtNumber(t.quantity, 4)} sh @ ${fmtCurrency(t.price, { decimals: 2 })} · ${fmtDate(t.transaction_date)}`;
            if (f !== 1) tip += `  |  split-adjusted: ${fmtNumber(t.quantity * f, 2)} sh @ ${fmtCurrency(t.price / f, { decimals: 2 })} (${splits.filter((s) => s.date > dk).map((s) => ratioText(s.ratio)).join(", ")})`;
            markers.push({ x: pt.date, y, color: isBuy ? "hsl(var(--gain))" : "hsl(var(--loss))",
                label: `${isBuy ? "B" : "S"}${f !== 1 ? "*" : ""}`, tip });
        }
    }

    const addCompare = () => {
        const t = addInput.trim().toUpperCase();
        if (t && !compare.includes(t) && t !== ticker.toUpperCase()) setCompare((c) => [...c, t]);
        setAddInput("");
    };

    // Unified chronological history: trades + split events, with a running share balance (for
    // simple display and verifying data integrity). Split applies before same-date trades.
    const historyRows = (() => {
        const allTxns = detail.data?.transactions || [];
        const evs = [
            ...splits.map((s) => ({ date: s.date, kind: "split", ratio: s.ratio, source: s.source })),
            ...allTxns.map((t) => ({ date: (t.transaction_date || "").slice(0, 10), kind: "txn", t })),
        ];
        const pr = (e) => (e.kind === "split" ? 0 : 1);
        evs.sort((a, b) => (a.date < b.date ? -1 : a.date > b.date ? 1 : pr(a) - pr(b)));
        let bal = 0;
        for (const e of evs) {
            if (e.kind === "split") bal *= e.ratio;
            else {
                const ty = (e.t.transaction_type || "").toUpperCase();
                if (ty === "BUY") bal += Number(e.t.quantity) || 0;
                else if (ty === "SELL") bal -= Number(e.t.quantity) || 0;
            }
            e.balance = Math.abs(bal) < 1e-9 ? 0 : bal;
        }
        return evs.reverse(); // newest first for display
    })();

    return (
        <div className="space-y-6">
            <Button variant="ghost" size="sm" onClick={() => navigate(-1)} className="-ml-2" data-testid="back-btn"><ArrowLeft className="w-4 h-4 mr-1.5" />Back</Button>

            {detail.isLoading ? <Skeleton className="h-32 rounded-xl" /> : (
                <div className="flex flex-wrap items-end justify-between gap-4">
                    <div>
                        <div className="flex items-center gap-3">
                            <h1 className="font-heading text-3xl font-bold tracking-tight num">{ticker.toUpperCase()}</h1>
                            {info?.sector && info.sector !== "Unknown" && <Badge variant="secondary">{info.sector}</Badge>}
                        </div>
                        <p className="text-muted-foreground">{info?.name}</p>
                    </div>
                    <div className="text-right">
                        <div className="num text-3xl font-bold">{quote ? fmtCurrency(quote.price, { decimals: 2 }) : "—"}</div>
                        {quote && <div className={`num text-sm ${gainClass(quote.change)}`}>{fmtCurrency(quote.change, { decimals: 2 })} ({fmtPct(quote.change_percent)}) today</div>}
                        <div className="text-[10px] text-muted-foreground mt-0.5">{quote?.source}</div>
                    </div>
                </div>
            )}

            {/* Your Position + Key Data — above the chart, compact */}
            <div className={`grid grid-cols-1 ${watchlist ? "" : "md:grid-cols-2"} gap-4`}>
                {/* Position */}
                {!watchlist && (
                <Card className="p-4">
                    <button onClick={() => setShowTrades((s) => !s)} className="w-full flex items-center justify-between mb-3 text-left" data-testid="position-card-toggle">
                        <h2 className="font-heading text-base font-semibold">Your Position</h2>
                        {pos && <span className="text-xs text-muted-foreground inline-flex items-center gap-1">{showTrades ? "Hide" : "View"} trades <ChevronDown className={`w-4 h-4 transition-transform ${showTrades ? "rotate-180" : ""}`} /></span>}
                    </button>
                    {pos ? (
                        <>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-3">
                            <Stat label="Shares" value={fmtNumber(pos.shares, 4)} />
                            <Stat label="Avg Cost" value={fmtCurrency(pos.avg_cost, { decimals: 2 })} />
                            <Stat label="Total Invested" value={mask(fmtCurrency(pos.cost_basis))} />
                            <Stat label="Market Value" value={pos.market_value != null ? mask(fmtCurrency(pos.market_value)) : "—"} />
                            <Stat label="Unrealized P/L" value={pos.unrealized_pl != null ? mask(fmtCurrency(pos.unrealized_pl)) : "—"} cls={gainClass(pos.unrealized_pl)} />
                            <Stat label="Unrealized P/L %" value={fmtPct(pos.unrealized_pl_pct)} cls={gainClass(pos.unrealized_pl_pct)} />
                        </div>
                        {showTrades && (
                            <div className="mt-4 border-t border-border pt-3" data-testid="position-trades">
                                <div className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Purchases &amp; Sales</div>
                                <div className="max-h-56 overflow-y-auto">
                                    <table className="w-full text-sm">
                                        <thead>
                                            <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                                <th className="py-1 font-medium text-left">Date</th>
                                                <th className="py-1 font-medium text-right">Shares</th>
                                                <th className="py-1 font-medium text-right">Price/Share</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {tradeTxns.map((t) => {
                                                const isBuy = (t.transaction_type || "").toUpperCase() === "BUY";
                                                return (
                                                    <tr key={t.id} className="border-t border-border/40" data-testid={`position-trade-${t.id}`}>
                                                        <td className="py-1.5 text-left">
                                                            <span className="inline-flex items-center gap-1.5">
                                                                <span className="w-2 h-2 rounded-full shrink-0" style={{ background: isBuy ? "hsl(var(--gain))" : "hsl(var(--loss))" }} />
                                                                <span className="num text-xs">{fmtDate(t.transaction_date)}</span>
                                                            </span>
                                                        </td>
                                                        <td className="py-1.5 text-right num">{fmtNumber(t.quantity, 4)}</td>
                                                        <td className="py-1.5 text-right num">{fmtCurrency(t.price, { decimals: 2 })}</td>
                                                    </tr>
                                                );
                                            })}
                                            {tradeTxns.length === 0 && <tr><td colSpan={3} className="py-3 text-muted-foreground text-xs">No purchases or sales.</td></tr>}
                                        </tbody>
                                    </table>
                                </div>
                                <p className="text-[10px] text-muted-foreground mt-2 flex items-center gap-3">
                                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "hsl(var(--gain))" }} /> Buy</span>
                                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "hsl(var(--loss))" }} /> Sell</span>
                                    · also marked on the chart below
                                </p>
                                {realizedSales.length > 0 && (
                                    <div className="mt-4 border-t border-border pt-3" data-testid="position-realized">
                                        <div className="flex items-center justify-between mb-2">
                                            <div className="text-[11px] uppercase tracking-wider text-muted-foreground">Realized Gains / Losses</div>
                                            <div className={`num text-xs font-semibold ${gainClass(realizedPl)}`} data-testid="realized-pl-total">{mask(fmtCurrency(realizedPl))}</div>
                                        </div>
                                        <div className="max-h-56 overflow-y-auto">
                                            <table className="w-full text-sm">
                                                <thead>
                                                    <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                                        <th className="py-1 font-medium text-left">Date</th>
                                                        <th className="py-1 font-medium text-right">Shares</th>
                                                        <th className="py-1 font-medium text-right">Proceeds</th>
                                                        <th className="py-1 font-medium text-right">Cost</th>
                                                        <th className="py-1 font-medium text-right">P/L</th>
                                                    </tr>
                                                </thead>
                                                <tbody>
                                                    {realizedSales.map((s, i) => (
                                                        <tr key={`${s.date}-${i}`} className="border-t border-border/40" data-testid={`realized-row-${i}`}>
                                                            <td className="py-1.5 text-left num text-xs">{fmtDate(s.date)}</td>
                                                            <td className="py-1.5 text-right num">{fmtNumber(s.quantity, 4)}</td>
                                                            <td className="py-1.5 text-right num">{mask(fmtCurrency(s.proceeds))}</td>
                                                            <td className="py-1.5 text-right num text-muted-foreground">{mask(fmtCurrency(s.cost_basis))}</td>
                                                            <td className={`py-1.5 text-right num font-medium ${gainClass(s.realized_pl)}`}>{mask(fmtCurrency(s.realized_pl))}</td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                        </>
                    ) : <p className="text-sm text-muted-foreground">You don't currently hold {ticker.toUpperCase()}.</p>}
                </Card>
                )}

                {/* Fundamentals */}
                <Card className="p-4">
                    <h2 className="font-heading text-base font-semibold mb-3">Key Data</h2>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-3">
                        <Stat label="Market Cap" value={info?.market_cap ? fmtCompact(info.market_cap) : "—"} />
                        <Stat label="P/E" value={info?.pe ? info.pe.toFixed(2) : "—"} />
                        <Stat label="PEG" value={info?.peg ? Number(info.peg).toFixed(2) : "—"} />
                        <Stat label="Beta" value={info?.beta ? info.beta.toFixed(2) : "—"} />
                        <Stat label="52W High" value={info?.week52high ? fmtCurrency(info.week52high, { decimals: 2 }) : "—"} />
                        <Stat label="52W Low" value={info?.week52low ? fmtCurrency(info.week52low, { decimals: 2 }) : "—"} />
                        <Stat label="Hist Vol (1Y)" value={info?.volatility != null ? `${info.volatility}%` : "—"} />
                        <Stat label="Implied Vol" value={info?.implied_volatility != null ? `${info.implied_volatility}%` : "N/A"} />
                    </div>
                </Card>
            </div>

            {/* Chart with benchmark overlays */}
            <Card className="p-4">
                <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
                    <h2 className="font-heading text-base font-semibold">Performance vs Benchmarks</h2>
                    <RangeSelector value={range} onChange={setRange} ranges={["1D", "1W", "1M", "3M", "6M", "YTD", "1Y", "ALL"]} testPrefix="sec-range" />
                </div>
                <div className="flex flex-wrap items-center gap-2 mb-4">
                    {seriesKeys.map((k, i) => (
                        <Badge key={k} variant="outline" className="gap-1.5 pr-1" style={{ borderColor: COMPARE_COLORS[i % COMPARE_COLORS.length] }} data-testid={`series-badge-${k}`}>
                            <span className="w-2 h-2 rounded-full" style={{ background: COMPARE_COLORS[i % COMPARE_COLORS.length] }} />{k}
                            {k !== ticker && <button onClick={() => setCompare((c) => c.filter((x) => x !== k))} className="ml-0.5 hover:text-loss" data-testid={`remove-series-${k}`}><X className="w-3 h-3" /></button>}
                        </Badge>
                    ))}
                    <div className="flex items-center gap-1">
                        <Input value={addInput} onChange={(e) => setAddInput(e.target.value.toUpperCase())} onKeyDown={(e) => e.key === "Enter" && addCompare()}
                            placeholder="Add ticker" className="h-7 w-28 num text-xs" data-testid="add-series-input" />
                        <Button size="icon" variant="outline" className="h-7 w-7" onClick={addCompare} data-testid="add-series-btn"><Plus className="w-3.5 h-3.5" /></Button>
                    </div>
                </div>
                {hist.isLoading ? <Skeleton className="h-[320px] rounded-lg" /> : <MultiLineChart dataKeys={seriesKeys} data={chartData} valueSuffix="%" height={320} intraday={intraday} colors={COMPARE_COLORS} markers={markers} />}
                <p className="text-xs text-muted-foreground mt-2">Normalized % change over selected period. NASDAQ Composite &amp; S&amp;P 500 shown by default.{!watchlist && <> <span className="inline-flex items-center gap-1 ml-1"><span className="w-2 h-2 rounded-full" style={{ background: "hsl(var(--gain))" }} /> buys</span> · <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: "hsl(var(--loss))" }} /> sells</span> marked on the {ticker.toUpperCase()} line.{anySplitAdj && <span className="block mt-1">* Trade predates a stock split — hover the point to see the <b>split-adjusted</b> shares &amp; price that line up with today's quote.</span>}</>}</p>
            </Card>

            {/* Volatility trend + Transactions */}
            <div className={`grid grid-cols-1 ${watchlist ? "" : "lg:grid-cols-2"} gap-6`}>
                <Card className="p-4">
                    <div className="flex items-center justify-between mb-1">
                        <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Volatility Trend · rolling 21d</span>
                        {vol.data?.current != null && <span className="num text-xs font-semibold text-accent">{vol.data.current}%</span>}
                    </div>
                    {vol.isLoading ? <div className="h-[90px]"><Skeleton className="h-full w-full rounded" /></div>
                        : <Sparkline data={vol.data?.series || []} dataKey="vol" />}
                </Card>

                {/* Transactions */}
                {!watchlist && (
                <Card className="p-4">
                    <h2 className="font-heading text-base font-semibold mb-1">Transaction &amp; Corporate Events</h2>
                    <p className="text-[11px] text-muted-foreground mb-3">Trades and stock splits in order, with the resulting share balance — useful for verifying your data.</p>
                    <div className="max-h-72 overflow-y-auto">
                        <table className="w-full text-sm" data-testid="ticker-history-table">
                            <thead>
                                <tr className="text-[10px] uppercase tracking-wide text-muted-foreground">
                                    <th className="text-left py-1 font-medium">Date</th>
                                    <th className="text-left py-1 font-medium">Event</th>
                                    <th className="text-right py-1 font-medium">Detail</th>
                                    <th className="text-right py-1 font-medium">Balance</th>
                                </tr>
                            </thead>
                            <tbody>
                                {historyRows.map((e, i) => (
                                    e.kind === "split" ? (
                                        <tr key={`sp-${i}`} className="border-t border-border/40 bg-primary/5" data-testid={`history-split-${e.date}`}>
                                            <td className="py-1.5 num text-muted-foreground text-xs">{fmtDate(e.date)}</td>
                                            <td className="py-1.5"><Badge variant="outline" className="text-[10px] border-primary/40 text-primary">SPLIT</Badge></td>
                                            <td className="py-1.5 text-right num">{ratioText(e.ratio)}{e.source === "manual" ? " (manual)" : ""}</td>
                                            <td className="py-1.5 text-right num font-medium">{fmtNumber(e.balance, 2)}</td>
                                        </tr>
                                    ) : (
                                        <tr key={e.t.id} className="border-t border-border/40" data-testid={`history-txn-${e.t.id}`}>
                                            <td className="py-1.5 num text-muted-foreground text-xs">{fmtDate(e.t.transaction_date)}</td>
                                            <td className="py-1.5"><Badge variant="outline" className={`text-[10px] ${TXN_BADGE[e.t.transaction_type]}`}>{e.t.transaction_type}</Badge></td>
                                            <td className="py-1.5 text-right num">{fmtNumber(e.t.quantity, 2)} @ {fmtCurrency(e.t.price, { decimals: 2 })}</td>
                                            <td className="py-1.5 text-right num font-medium">{["BUY", "SELL"].includes((e.t.transaction_type || "").toUpperCase()) ? fmtNumber(e.balance, 2) : "—"}</td>
                                        </tr>
                                    )
                                ))}
                            </tbody>
                        </table>
                        {historyRows.length === 0 && <p className="text-sm text-muted-foreground">No transactions.</p>}
                    </div>
                </Card>
                )}
            </div>

            {/* Notes + Price Alerts (held or watched) */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <NotesCard ticker={ticker.toUpperCase()} />
                <AlertsCard ticker={ticker.toUpperCase()} currentPrice={quote?.price} />
            </div>
        </div>
    );
}
